import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { toast } from 'react-toastify';
import BottomSheet from '../BottomSheet';
import { colors, fontStyles, spacing, borderRadius } from '../../styles';
import * as stores from '../../stores';
import { calculateSavingsPercentage } from '../../utils/pricing';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';

interface ManageSubscriptionSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const isAndroidPlatform = () => Capacitor.getPlatform() === 'android';

const ManageSubscriptionSheet: FC<ManageSubscriptionSheetProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { isTrialPeriod, planType, price, yearlyPrice, weeklyPrice, currencySymbol, trialDaysLeftText, formattedDate } =
    useSubscriptionStatus();

  const savingsPercentage = calculateSavingsPercentage(weeklyPrice, yearlyPrice);

  const openPlatformSubscriptions = () => {
    window.location.href = isAndroidPlatform()
      ? 'https://play.google.com/store/account/subscriptions'
      : 'https://apps.apple.com/account/subscriptions';
  };

  const handleUpgradeToYearly = async () => {
    try {
      const offerings = stores.Purchase.getRawState().offerings;
      const yearlyPackage = offerings?.current?.annual;

      if (!yearlyPackage) {
        toast.error(t('subscription.unableToFindYearlyPlan'));
        return;
      }

      const { customerInfo } = await Purchases.purchasePackage({ aPackage: yearlyPackage });

      if (customerInfo.entitlements.active[stores.entitlementIdentifier]?.isActive) {
        stores.Purchase.update((s) => {
          s.purchaserInfo = customerInfo;
        });
        toast.success(t('subscription.upgradedToYearly'));
        onClose();
      }
    } catch (error: any) {
      if (error?.userCancelled) {
        // User cancelled, don't show error
        return;
      }
      console.error('Failed to upgrade:', error);
      toast.error(t('subscription.upgradeFailed'));
    }
  };

  const handleSwitchToWeekly = () => {
    // Switching from yearly to weekly requires going through the platform
    openPlatformSubscriptions();
  };

  const handleCancel = () => {
    openPlatformSubscriptions();
  };

  // Header text based on subscription state
  const getHeaderText = () => {
    if (isTrialPeriod) {
      return `${t('subscription.freeTrialLabel')} \u00B7 ${trialDaysLeftText}`;
    }
    if (planType === 'yearly') {
      return `${t('subscription.bloomPro').toUpperCase()} \u00B7 ${t('subscription.yearly').toUpperCase()}`;
    }
    return `${t('subscription.bloomPro').toUpperCase()} \u00B7 ${t('subscription.weekly').toUpperCase()}`;
  };

  const getSubheaderText = () => {
    if (isTrialPeriod) {
      return `${t('subscription.convertsToYearly', { price: `${currencySymbol}${yearlyPrice}` })}${formattedDate ? ` ${t('subscription.onDate', { date: formattedDate })}` : ''}`;
    }
    return `${price}${formattedDate ? ` \u00B7 ${t('subscription.renewsDate', { date: formattedDate })}` : ''}`;
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div
        css={css`
          padding: ${spacing.centi} 0;
        `}
      >
        {/* Header */}
        <div
          css={css`
            text-align: center;
            margin-bottom: ${spacing.deci};
          `}
        >
          <p
            css={css`
              ${fontStyles.lion};
              color: ${colors.liquorice};
              margin: 0 0 ${spacing.milli} 0;
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {getHeaderText()}
          </p>
          <p
            css={css`
              ${fontStyles.cat};
              color: ${colors.silver};
              margin: 0;
              html[data-theme='dark'] & {
                color: ${colors.cloud};
              }
            `}
          >
            {getSubheaderText()}
          </p>
        </div>

        {/* Divider */}
        <div
          css={css`
            border-top: 1px solid ${colors.cloud};
            margin-bottom: ${spacing.deci};
            html[data-theme='dark'] & {
              border-color: ${colors.tungsten}40;
            }
          `}
        />

        {/* Action buttons */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${spacing.centi};
          `}
        >
          {/* Upgrade to Yearly (show for weekly subscribers) */}
          {planType === 'weekly' && (
            <button
              onClick={handleUpgradeToYearly}
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                width: 100%;
                background-color: ${colors.sugar};
                border: 1px solid ${colors.cloud};
                border-radius: ${borderRadius};
                padding: ${spacing.deci};
                cursor: pointer;
                text-align: left;
                html[data-theme='dark'] & {
                  background-color: ${colors.liquorice};
                  border-color: ${colors.tungsten};
                }
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  width: 100%;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: ${spacing.centi};
                  `}
                >
                  <span
                    css={css`
                      ${fontStyles.dog};
                    `}
                  >
                    &#8593;
                  </span>
                  <span
                    css={css`
                      ${fontStyles.lion};
                      color: ${colors.liquorice};
                      html[data-theme='dark'] & {
                        color: ${colors.sugar};
                      }
                    `}
                  >
                    {t('subscription.upgradeToYearly')}
                  </span>
                </div>
                <span
                  css={css`
                    ${fontStyles.mouse};
                    color: ${colors.snow};
                    background-color: ${colors.petrolBlue};
                    padding: ${spacing.milli} ${spacing.centi};
                    border-radius: ${borderRadius};
                  `}
                >
                  {t('subscription.savePercent', { percent: savingsPercentage })}
                </span>
              </div>
              <p
                css={css`
                  ${fontStyles.cat};
                  color: ${colors.silver};
                  margin: ${spacing.milli} 0 0 ${spacing.regular};
                  html[data-theme='dark'] & {
                    color: ${colors.cloud};
                  }
                `}
              >
                {t('subscription.perYear', { price: `${currencySymbol}${yearlyPrice}` })}
                {yearlyPrice > 0
                  ? ` (${t('subscription.perWeek', { price: `${currencySymbol}${Math.round((yearlyPrice / 52) * 100) / 100}` })})`
                  : ''}
              </p>
            </button>
          )}

          {/* Switch to Weekly (show for yearly subscribers and trial users) */}
          {(planType === 'yearly' || isTrialPeriod) && (
            <button
              onClick={handleSwitchToWeekly}
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                width: 100%;
                background-color: ${colors.sugar};
                border: 1px solid ${colors.cloud};
                border-radius: ${borderRadius};
                padding: ${spacing.deci};
                cursor: pointer;
                text-align: left;
                html[data-theme='dark'] & {
                  background-color: ${colors.liquorice};
                  border-color: ${colors.tungsten};
                }
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: ${spacing.centi};
                `}
              >
                <span
                  css={css`
                    ${fontStyles.dog};
                  `}
                >
                  &#8595;
                </span>
                <span
                  css={css`
                    ${fontStyles.lion};
                    color: ${colors.liquorice};
                    html[data-theme='dark'] & {
                      color: ${colors.sugar};
                    }
                  `}
                >
                  {t('subscription.switchToWeekly')}
                </span>
              </div>
              <p
                css={css`
                  ${fontStyles.cat};
                  color: ${colors.silver};
                  margin: ${spacing.milli} 0 0 ${spacing.regular};
                  html[data-theme='dark'] & {
                    color: ${colors.cloud};
                  }
                `}
              >
                {currencySymbol}
                {weeklyPrice}/{t('subscription.week')}{' '}
                {isTrialPeriod
                  ? `\u00B7 ${t('subscription.noCommitment')}`
                  : `\u00B7 ${t('subscription.changesAtRenewal')}`}
              </p>
            </button>
          )}

          {/* Cancel Subscription */}
          <button
            onClick={handleCancel}
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.centi};
              width: 100%;
              background-color: ${colors.sugar};
              border: 1px solid ${colors.cloud};
              border-radius: ${borderRadius};
              padding: ${spacing.deci};
              cursor: pointer;
              text-align: left;
              html[data-theme='dark'] & {
                background-color: ${colors.liquorice};
                border-color: ${colors.tungsten};
              }
            `}
          >
            <span
              css={css`
                ${fontStyles.dog};
                color: ${colors.berry};
              `}
            >
              &#10005;
            </span>
            <span
              css={css`
                ${fontStyles.lion};
                color: ${colors.liquorice};
                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              {isTrialPeriod ? t('subscription.cancelTrial') : t('subscription.cancelSubscription')}
            </span>
          </button>
        </div>

        {/* Footer */}
        <p
          css={css`
            ${fontStyles.cat};
            color: ${colors.silver};
            text-align: center;
            margin: ${spacing.deci} 0 0 0;
            html[data-theme='dark'] & {
              color: ${colors.cloud};
            }
          `}
        >
          {t('subscription.questionsContactFounder')}
          <br />
          <a
            href="mailto:eric@investwithbloom.com"
            css={css`
              color: ${colors.petrolBlue};
              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            `}
          >
            eric@investwithbloom.com
          </a>
        </p>
      </div>
    </BottomSheet>
  );
};

export default ManageSubscriptionSheet;
